import React from 'react';
import PublicLayout from '../../components/layouts/PublicLayout';
import { useSEO, generateBreadcrumbSchema, generateWebPageSchema } from '../../hooks/useSEO';
import { Download, Copy, Check } from 'lucide-react';

const BrandPage: React.FC = () => {
    const [copiedColor, setCopiedColor] = React.useState<string | null>(null);

    useSEO({
        title: "Brand Guidelines - NexuCapital",
        description: "Diretrizes oficiais da marca NexuCapital. Cores, tipografia, logotipos e padrões visuais.",
        path: "/brand",
        language: "pt-br",
        structuredData: [
            generateBreadcrumbSchema([
                { name: 'Home', url: '/' },
                { name: 'Brand', url: '/brand' }
            ]),
            generateWebPageSchema({
                name: "Brand Guidelines - NexuCapital",
                description: "Diretrizes oficiais da marca NexuCapital. Cores, tipografia, logotipos e padrões visuais.",
                url: "/brand",
                datePublished: "2025-01-20",
                dateModified: "2025-01-20"
            })
        ]
    });

    const copyToClipboard = (text: string, colorName: string) => {
        navigator.clipboard.writeText(text);
        setCopiedColor(colorName);
        setTimeout(() => setCopiedColor(null), 2000);
    };

    const primaryColors = [
        { name: 'Teal Profundo', hex: '#0f766e', rgb: 'rgb(15, 118, 110)', usage: 'Cor principal' },
        { name: 'Teal Escuro', hex: '#0d5c55', rgb: 'rgb(13, 92, 85)', usage: 'Hover, gradientes' },
        { name: 'Teal Claro', hex: '#14b8a6', rgb: 'rgb(20, 184, 166)', usage: 'Acentos' },
    ];

    const secondaryColors = [
        { name: 'Dourado Mineral', hex: '#c9a227', rgb: 'rgb(201, 162, 39)', usage: 'Destaques premium' },
        { name: 'Dourado Escuro', hex: '#a1811f', rgb: 'rgb(161, 129, 31)', usage: 'Hover' },
    ];

    const neutralColors = [
        { name: 'Grafite', hex: '#1c1e22', rgb: 'rgb(28, 30, 34)', usage: 'Textos, fundos escuros' },
        { name: 'Grafite Claro', hex: '#2b2e34', rgb: 'rgb(43, 46, 52)', usage: 'Cards escuros' },
        { name: 'Superfície', hex: '#23262b', rgb: 'rgb(35, 38, 43)', usage: 'Backgrounds' },
        { name: 'Branco', hex: '#ffffff', rgb: 'rgb(255, 255, 255)', usage: 'Fundos claros' },
        { name: 'Cinza Suave', hex: '#f3f4f6', rgb: 'rgb(243, 244, 246)', usage: 'Fundos secundários' },
        { name: 'Cinza', hex: '#8a8f98', rgb: 'rgb(138, 143, 152)', usage: 'Textos secundários' },
    ];

    const logos = [
        { name: 'Principal', file: 'logo-nexucapital-principal.png', usage: 'Uso geral em fundos claros' },
        { name: 'Principal Branca', file: 'logo-nexucapital-principal-branca.png', usage: 'Fundos escuros, hero sections' },
        { name: 'Ícone', file: 'logo-nexucapital-icone.png', usage: 'Favicon, avatares, apps' },
        { name: 'Texto', file: 'logo-nexucapital-texto.png', usage: 'Quando ícone já está presente' },
        { name: 'Vertical', file: 'logo-nexucapital-vertical.png', usage: 'Banners, materiais impressos' },
        { name: 'Preto', file: 'logo-nexucapital-preto.png', usage: 'Documentos monocromáticos' },
        { name: 'Branco', file: 'logo-nexucapital-branco.png', usage: 'Fundos escuros, versão negativa' },
    ];

    const ColorCard = ({ color, isDark = false }: { color: typeof primaryColors[0], isDark?: boolean }) => (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow">
            <div
                className="h-24 flex items-end p-3"
                style={{ backgroundColor: color.hex }}
            >
                <span className={`text-xs font-mono ${isDark ? 'text-white/80' : 'text-black/60'}`}>
                    {color.hex}
                </span>
            </div>
            <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-dark">{color.name}</h4>
                    <button
                        onClick={() => copyToClipboard(color.hex, color.name)}
                        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                        title="Copiar código hex"
                    >
                        {copiedColor === color.name ? (
                            <Check size={16} className="text-green-500" />
                        ) : (
                            <Copy size={16} className="text-gray-400" />
                        )}
                    </button>
                </div>
                <p className="text-xs text-gray-500 font-mono mb-1">{color.rgb}</p>
                <p className="text-sm text-gray-600">{color.usage}</p>
            </div>
        </div>
    );

    return (
        <PublicLayout>
            <main className="flex-grow">
                {/* Hero Section */}
                <div className="bg-gradient-to-br from-dark to-primary-dark text-white py-16 md:py-24 relative overflow-hidden">
                    <div
                        className="absolute inset-0 opacity-20 pointer-events-none"
                        style={{
                            backgroundImage: `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.05)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)" /></svg>')`
                        }}
                    />
                    <div className="container mx-auto px-4 text-center relative z-10">
                        <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold mb-4 md:mb-6 animate-fade-in-up bg-gradient-to-r from-white to-secondary bg-clip-text text-transparent">
                            Brand Guidelines
                        </h1>
                        <p className="text-base sm:text-lg md:text-xl max-w-3xl mx-auto text-gray-300">
                            Diretrizes oficiais da marca NexuCapital para uso interno, media kit e registro.
                        </p>
                    </div>
                </div>

                {/* Essência da Marca */}
                <section className="py-16 md:py-20 bg-white">
                    <div className="container mx-auto px-4">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-2xl md:text-3xl font-serif font-bold text-dark mb-2">Essência da Marca</h2>
                            <div className="w-16 h-1 bg-gradient-to-r from-primary to-secondary mb-8"></div>

                            <div className="space-y-8">
                                <div>
                                    <h3 className="text-lg font-semibold text-primary mb-2">Tagline</h3>
                                    <p className="text-xl text-gray-700 italic border-l-4 border-secondary pl-4">
                                        "A ponte confiável entre mineração responsável e capital global"
                                    </p>
                                </div>

                                <div className="grid md:grid-cols-2 gap-8">
                                    <div className="bg-gray-50 p-6 rounded-xl">
                                        <h3 className="text-lg font-semibold text-primary mb-3">Missão</h3>
                                        <p className="text-gray-700">
                                            Desbloquear e maximizar o valor de ativos minerários e produtos da mineração, atuando como um parceiro estratégico e inovador para nossos clientes.
                                        </p>
                                    </div>
                                    <div className="bg-gray-50 p-6 rounded-xl">
                                        <h3 className="text-lg font-semibold text-primary mb-3">Visão</h3>
                                        <p className="text-gray-700">
                                            Ser reconhecida como a escolha prioritária e mais confiável de cada cliente apoiado pela NexuCapital.
                                        </p>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-primary mb-4">Valores</h3>
                                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {[
                                            { title: 'Maximização de Recursos', desc: 'Estratégias inteligentes para retornos superiores' },
                                            { title: 'Respeito Humanizado', desc: 'Ética em cada interação' },
                                            { title: 'Confiabilidade', desc: 'Processos rigorosos e clareza total' },
                                            { title: 'Foco no Cliente', desc: 'Objetivos do cliente como missão' },
                                            { title: 'Valor Integrado', desc: 'União de expertise e valores éticos' },
                                        ].map((value, i) => (
                                            <div key={i} className="bg-white border border-gray-200 p-4 rounded-lg">
                                                <h4 className="font-semibold text-dark mb-1">{value.title}</h4>
                                                <p className="text-sm text-gray-600">{value.desc}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Paleta de Cores */}
                <section className="py-16 md:py-20 bg-gray-50">
                    <div className="container mx-auto px-4">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-2xl md:text-3xl font-serif font-bold text-dark mb-2">Paleta de Cores</h2>
                            <div className="w-16 h-1 bg-gradient-to-r from-primary to-secondary mb-8"></div>

                            <div className="space-y-10">
                                <div>
                                    <h3 className="text-lg font-semibold text-primary mb-4">Cores Primárias</h3>
                                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {primaryColors.map((color) => (
                                            <ColorCard key={color.hex} color={color} isDark />
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-secondary mb-4">Cores Secundárias</h3>
                                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {secondaryColors.map((color) => (
                                            <ColorCard key={color.hex} color={color} isDark />
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Cores Neutras</h3>
                                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {neutralColors.map((color) => (
                                            <ColorCard key={color.hex} color={color} isDark={color.hex.startsWith('#1') || color.hex.startsWith('#2')} />
                                        ))}
                                    </div>
                                </div>

                                <div className="bg-white p-6 rounded-xl border border-gray-200">
                                    <h3 className="text-lg font-semibold text-dark mb-4">Significado das Cores</h3>
                                    <div className="grid md:grid-cols-3 gap-4">
                                        <div className="flex items-start gap-3">
                                            <div className="w-4 h-4 rounded-full bg-primary mt-1"></div>
                                            <div>
                                                <p className="font-medium text-dark">Teal</p>
                                                <p className="text-sm text-gray-600">Confiança, estabilidade, profissionalismo</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <div className="w-4 h-4 rounded-full bg-secondary mt-1"></div>
                                            <div>
                                                <p className="font-medium text-dark">Dourado</p>
                                                <p className="text-sm text-gray-600">Valor, premium, riqueza mineral</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <div className="w-4 h-4 rounded-full bg-dark mt-1"></div>
                                            <div>
                                                <p className="font-medium text-dark">Grafite</p>
                                                <p className="text-sm text-gray-600">Solidez, seriedade, modernidade</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Tipografia */}
                <section className="py-16 md:py-20 bg-white">
                    <div className="container mx-auto px-4">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-2xl md:text-3xl font-serif font-bold text-dark mb-2">Tipografia</h2>
                            <div className="w-16 h-1 bg-gradient-to-r from-primary to-secondary mb-8"></div>

                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="bg-gray-50 p-6 rounded-xl">
                                    <h3 className="text-lg font-semibold text-primary mb-4">Fonte Display</h3>
                                    <p className="font-serif text-4xl text-dark mb-4">Playfair Display</p>
                                    <p className="text-gray-600 mb-4">Fonte serifada premium para títulos</p>
                                    <div className="space-y-2 text-gray-700">
                                        <p className="font-serif text-3xl">H1 - Títulos principais</p>
                                        <p className="font-serif text-2xl">H2 - Títulos de seção</p>
                                        <p className="font-serif text-xl">H3 - Subtítulos</p>
                                    </div>
                                </div>

                                <div className="bg-gray-50 p-6 rounded-xl">
                                    <h3 className="text-lg font-semibold text-primary mb-4">Fonte Corpo</h3>
                                    <p className="text-4xl text-dark mb-4">Inter</p>
                                    <p className="text-gray-600 mb-4">Fonte sans-serif moderna para interface</p>
                                    <div className="space-y-2 text-gray-700">
                                        <p className="font-light">Light (300) - Descrições longas</p>
                                        <p className="font-normal">Regular (400) - Corpo de texto</p>
                                        <p className="font-semibold">Semi-Bold (600) - Botões</p>
                                        <p className="font-bold">Bold (700) - Destaques</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Logotipos */}
                <section className="py-16 md:py-20 bg-gray-50">
                    <div className="container mx-auto px-4">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-2xl md:text-3xl font-serif font-bold text-dark mb-2">Logotipos</h2>
                            <div className="w-16 h-1 bg-gradient-to-r from-primary to-secondary mb-8"></div>

                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
                                {logos.map((logo) => (
                                    <div key={logo.file} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow">
                                        <div className={`h-32 flex items-center justify-center p-4 ${logo.name.includes('Branca') || logo.name === 'Branco' ? 'bg-dark' : 'bg-gray-100'}`}>
                                            <img
                                                src={`/assets/logos/${logo.file}`}
                                                alt={`Logo ${logo.name}`}
                                                className="max-h-full max-w-full object-contain"
                                            />
                                        </div>
                                        <div className="p-4">
                                            <div className="flex items-center justify-between mb-2">
                                                <h4 className="font-semibold text-dark">{logo.name}</h4>
                                                <a
                                                    href={`/assets/logos/${logo.file}`}
                                                    download
                                                    className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                                                    title="Download"
                                                >
                                                    <Download size={16} className="text-gray-400" />
                                                </a>
                                            </div>
                                            <p className="text-sm text-gray-600">{logo.usage}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="bg-white p-6 rounded-xl border border-gray-200">
                                    <h3 className="text-lg font-semibold text-green-600 mb-3">Uso Permitido</h3>
                                    <ul className="space-y-2 text-gray-700">
                                        <li className="flex items-start gap-2">
                                            <span className="text-green-500 mt-1">✓</span>
                                            Logo colorido em fundos brancos ou cinza claro
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span className="text-green-500 mt-1">✓</span>
                                            Logo branco em fundos escuros ou imagens com overlay
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span className="text-green-500 mt-1">✓</span>
                                            Logo preto em documentos monocromáticos
                                        </li>
                                    </ul>
                                </div>

                                <div className="bg-white p-6 rounded-xl border border-gray-200">
                                    <h3 className="text-lg font-semibold text-red-600 mb-3">Proibições</h3>
                                    <ul className="space-y-2 text-gray-700">
                                        <li className="flex items-start gap-2">
                                            <span className="text-red-500 mt-1">✗</span>
                                            Distorcer proporções do logo
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span className="text-red-500 mt-1">✗</span>
                                            Alterar cores originais
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span className="text-red-500 mt-1">✗</span>
                                            Adicionar efeitos (sombra, brilho, contorno)
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span className="text-red-500 mt-1">✗</span>
                                            Rotacionar o logo
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Elementos Visuais */}
                <section className="py-16 md:py-20 bg-white">
                    <div className="container mx-auto px-4">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-2xl md:text-3xl font-serif font-bold text-dark mb-2">Elementos Visuais</h2>
                            <div className="w-16 h-1 bg-gradient-to-r from-primary to-secondary mb-8"></div>

                            <div className="space-y-8">
                                <div>
                                    <h3 className="text-lg font-semibold text-primary mb-4">Gradientes Oficiais</h3>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div className="rounded-xl overflow-hidden">
                                            <div className="h-20 bg-gradient-to-r from-white to-secondary"></div>
                                            <div className="bg-gray-50 p-3">
                                                <p className="font-medium text-sm text-dark">Gradiente de Texto</p>
                                                <p className="text-xs text-gray-500 font-mono">from-white to-secondary</p>
                                            </div>
                                        </div>
                                        <div className="rounded-xl overflow-hidden">
                                            <div className="h-20 bg-gradient-to-r from-primary to-secondary"></div>
                                            <div className="bg-gray-50 p-3">
                                                <p className="font-medium text-sm text-dark">Gradiente Accent</p>
                                                <p className="text-xs text-gray-500 font-mono">from-primary to-secondary</p>
                                            </div>
                                        </div>
                                        <div className="rounded-xl overflow-hidden">
                                            <div className="h-20 bg-gradient-to-br from-dark to-primary-dark"></div>
                                            <div className="bg-gray-50 p-3">
                                                <p className="font-medium text-sm text-dark">Gradiente Overlay</p>
                                                <p className="text-xs text-gray-500 font-mono">from-dark to-primary-dark</p>
                                            </div>
                                        </div>
                                        <div className="rounded-xl overflow-hidden">
                                            <div className="h-20 bg-gradient-to-br from-primary/90 to-primary-dark/85"></div>
                                            <div className="bg-gray-50 p-3">
                                                <p className="font-medium text-sm text-dark">Gradiente Cards</p>
                                                <p className="text-xs text-gray-500 font-mono">from-primary/90 to-primary-dark/85</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-primary mb-4">Espaçamentos</h3>
                                    <div className="bg-gray-50 p-6 rounded-xl">
                                        <div className="grid grid-cols-5 gap-4 text-center">
                                            {[
                                                { token: 'XS', value: '8px' },
                                                { token: 'SM', value: '16px' },
                                                { token: 'MD', value: '32px' },
                                                { token: 'LG', value: '64px' },
                                                { token: 'XL', value: '96px' },
                                            ].map((space) => (
                                                <div key={space.token}>
                                                    <div className="font-semibold text-dark">{space.token}</div>
                                                    <div className="text-sm text-gray-600">{space.value}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-primary mb-4">Bordas</h3>
                                    <div className="flex gap-6">
                                        <div className="flex items-center gap-3">
                                            <div className="w-16 h-16 bg-gray-200 rounded-lg"></div>
                                            <div>
                                                <p className="font-medium text-dark">Padrão</p>
                                                <p className="text-sm text-gray-600">8px</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="w-16 h-16 bg-gray-200 rounded-xl"></div>
                                            <div>
                                                <p className="font-medium text-dark">Cards</p>
                                                <p className="text-sm text-gray-600">16px</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Download Section */}
                <section className="py-16 md:py-20 bg-gradient-to-br from-primary to-primary-dark text-white">
                    <div className="container mx-auto px-4 text-center">
                        <h2 className="text-2xl md:text-3xl font-serif font-bold mb-4">Download do Brandbook</h2>
                        <p className="text-gray-200 mb-8 max-w-xl mx-auto">
                            Baixe o documento completo com todas as diretrizes da marca em formato Markdown.
                        </p>
                        <a
                            href="/docs/brand/BRANDBOOK.md"
                            download
                            className="inline-flex items-center gap-2 bg-secondary hover:bg-secondary-dark text-white px-8 py-3 rounded-full font-semibold transition-all hover:scale-105"
                        >
                            <Download size={20} />
                            Download Brandbook (MD)
                        </a>
                    </div>
                </section>

                {/* Contato */}
                <section className="py-12 bg-gray-50">
                    <div className="container mx-auto px-4 text-center">
                        <p className="text-gray-600">
                            Dúvidas sobre o uso da marca? Entre em contato: {' '}
                            <a href="mailto:contato@nexucapital.com.br" className="text-primary hover:text-primary-dark font-medium">
                                contato@nexucapital.com.br
                            </a>
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                            © 2025 NexuCapital. Todos os direitos reservados.
                        </p>
                    </div>
                </section>
            </main>
        </PublicLayout>
    );
};

export default BrandPage;
